import React, { useState, useEffect } from 'react'
import { usePrompt } from '../contexts/PromptContext'
import { WritingPrompt } from '../types/models'
import '../styles/Auth.css'

interface PromptSelectorProps {
  yearLevel: number
  onSelectPrompt: (prompt: WritingPrompt) => void
  onBack: () => void
}

export const PromptSelector: React.FC<PromptSelectorProps> = ({
  yearLevel,
  onSelectPrompt,
  onBack
}) => {
  const { prompts, loading, error, getPrompts, getRandomPrompt } = usePrompt()
  const [testFormat, setTestFormat] = useState<'edutest' | 'acer'>('edutest')
  const [category, setCategory] = useState<string>('creative')
  const [selectedPromptId, setSelectedPromptId] = useState<string>('')
  const [loadingRandom, setLoadingRandom] = useState(false)

  useEffect(() => {
    getPrompts(yearLevel, testFormat, category)
  }, [yearLevel, testFormat, category, getPrompts])

  // Reset selected prompt when prompts change
  useEffect(() => {
    setSelectedPromptId('')
  }, [prompts])

  const selectedPrompt = prompts.find(p => p.id === selectedPromptId)

  const handleRandomPrompt = async () => {
    setLoadingRandom(true)
    try {
      const randomPrompt = await getRandomPrompt(yearLevel, testFormat, category)
      if (randomPrompt) {
        // Find if this prompt is in our list and select it, or just use it directly
        const foundPrompt = prompts.find(p => p.id === randomPrompt.id)
        if (foundPrompt) {
          setSelectedPromptId(foundPrompt.id)
        } else {
          // If not in list (edge case), select it directly
          onSelectPrompt(randomPrompt)
        }
      }
    } finally {
      setLoadingRandom(false)
    }
  }

  const handleStartWriting = () => {
    if (selectedPrompt) {
      onSelectPrompt(selectedPrompt)
    }
  }

  return (
    <div className="prompt-selector">
      <div className="prompt-selector-header">
        <button onClick={onBack} className="btn-secondary">
          ← Back to Profiles
        </button>
        <h2>Select a Writing Prompt</h2>
        <p>Year Level: {yearLevel}</p>
      </div>

      <div className="prompt-filters">
        <div className="filter-group">
          <label>Test Format:</label>
          <select 
            value={testFormat} 
            onChange={(e) => setTestFormat(e.target.value as 'edutest' | 'acer')}
            className="filter-select"
          >
            <option value="edutest">Edutest</option>
            <option value="acer">ACER</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Category:</label>
          <select 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            className="filter-select"
          >
            <option value="creative">Creative</option>
            <option value="persuasive">Persuasive</option>
            <option value="narrative">Narrative</option>
          </select>
        </div>
      </div>

      {loading && <div className="loading">Loading prompts...</div>}
      
      {error && <div className="error-message">{error}</div>}

      {!loading && prompts.length === 0 && (
        <div className="empty-state">
          <p>No prompts available for the selected criteria.</p>
          <p>Try changing the test format or category.</p>
        </div>
      )}

      {!loading && prompts.length > 0 && (
        <div className="prompt-selection-area">
          <div className="prompt-selection-methods">
            <div className="selection-method">
              <label>Choose a prompt:</label>
              <select 
                value={selectedPromptId} 
                onChange={(e) => setSelectedPromptId(e.target.value)}
                className="prompt-dropdown"
              >
                <option value="">-- Select a prompt --</option>
                {prompts.map((prompt, index) => (
                  <option key={prompt.id} value={prompt.id}>
                    {index + 1}. {prompt.promptText.substring(0, 60)}...
                  </option>
                ))}
              </select>
            </div>

            <div className="selection-divider">
              <span>OR</span>
            </div>

            <div className="selection-method">
              <button 
                onClick={handleRandomPrompt}
                className="btn-random"
                disabled={loadingRandom || loading}
              >
                {loadingRandom ? '🎲 Loading...' : '🎲 Pick Random Prompt'}
              </button>
            </div>
          </div>

          {selectedPrompt && (
            <div className="selected-prompt-preview">
              <div className="prompt-card">
                <div className="prompt-card-header">
                  <span className="prompt-badge">{selectedPrompt.category}</span>
                  <span className="prompt-time">{selectedPrompt.timeLimit} min</span>
                </div>
                <p className="prompt-text">{selectedPrompt.promptText}</p>
                {selectedPrompt.context && (
                  <p className="prompt-context">{selectedPrompt.context}</p>
                )}
                <p className="prompt-instructions">{selectedPrompt.instructions}</p>
                <button 
                  onClick={handleStartWriting}
                  className="btn-primary btn-start-writing"
                >
                  Start Writing
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
